import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { PageHeader, PageWrapper, PopupTypes } from '../components/partials';
import TopArea from './components/TopArea';
import { FAQ_LINKS, MIX_PANEL_KEYS, TRACK_EVENT_PREFIX } from './consts';
import ABTestingTable from './components/ABTestingTable';
import { GET_AB_TESTING_METADATA } from './queries';
import { DELETE_AB_TEST } from './mutations';
import GeneralPopup from '../components/organisms/GeneralPopup';
import WizardWrapper from './components/Wizard/WizardWrapper';
import { getEmptyStateOrgData } from '../selectors/onboarding';
import { getAbTestingEmptyStateData, shouldShowEmptyState } from './emptyStateUtils';
import { Spinner } from '../components/widgets';
import Reporting from './components/Reporting/Reporting';
import { trackMixpanelEvent } from '../utils/general';

export default function ABTesting() {
    const [wizardOpen, setWizardOpen] = useState(false);
    const [deletedTestId, setDeletedTestId] = useState(null);
    const [testId, setTestId] = useState(null);
    const getTableTests = useQuery(GET_AB_TESTING_METADATA, {
        fetchPolicy: 'network-only',
    });

    const [deleteAbTest, deleteAbTestResponse] = useMutation(DELETE_AB_TEST, {
        onCompleted: () => {
            getTableTests.refetch();
            deleteAbTestResponse.reset();
            setDeletedTestId(null);
            setTestId(null);
        },
        onError: () => {
            deleteAbTestResponse.reset();
            setDeletedTestId(null);
            setTestId(null);
        },
    });

    const onTestClick = useCallback(test => {
        setTestId(test.id);
        setWizardOpen(false);
    }, []);

    const onCloseTest = useCallback(() => {
        setTestId(null);
    }, []);

    useEffect(() => trackMixpanelEvent(TRACK_EVENT_PREFIX, MIX_PANEL_KEYS.PAGE_VISIT), []);

    const emptyStateOrgData = useSelector(state => getEmptyStateOrgData(state));
    if (!emptyStateOrgData) {
        return <Spinner show expanded />;
    }

    const emptyState = getAbTestingEmptyStateData(emptyStateOrgData);
    const showTasksEmptyState = shouldShowEmptyState(emptyStateOrgData);

    const onDeleteConfirm = async () => {
        await deleteAbTest({ variables: { testId: deletedTestId } });
    };

    const onEditClicked = async test => {
        setTestId(test.id);
        setWizardOpen(true);
    };

    const onDeleteClicked = test => {
        setDeletedTestId(test.id);
    };

    const onCreateNew = () => {
        setTestId(null);
        setWizardOpen(true);
    };

    const onClose = () => {
        setTestId(null);
        setWizardOpen(false);
    };
    if (wizardOpen) {
        return <WizardWrapper refetchTable={getTableTests.refetch} onClose={onClose} testId={testId} />;
    }

    if (testId) {
        return (
            <>
                <Reporting
                    onDeleteClick={onDeleteClicked}
                    onEditClick={onEditClicked}
                    testId={testId}
                    onClose={onCloseTest}
                />
                <GeneralPopup
                    open={!!deletedTestId}
                    type={PopupTypes.DELETE}
                    title="STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.DELETE_WARNING"
                    acceptText="STATIC.BUTTONS.DELETE"
                    onAccept={onDeleteConfirm}
                    onReject={() => setDeletedTestId(null)}
                    loading={deleteAbTestResponse.loading}
                />
            </>
        );
    }

    return (
        <>
            <PageWrapper>
                <PageHeader
                    emptyStateOrgData={emptyStateOrgData}
                    emptyStateHeaderData={emptyState}
                    showEmptyState={showTasksEmptyState}
                    showEmptyStateCloseIcon={false}
                    imageSrc={emptyState.imageSrc}
                    subText={FAQ_LINKS}
                    text="STATIC.PAGE_HEADERS.AUDIENCE_AB_TESTING"
                />
                <TopArea createDisabled={showTasksEmptyState} onCreateNew={onCreateNew} />
                <ABTestingTable onTestClick={onTestClick} onEdit={onEditClicked} onDelete={onDeleteClicked} />
            </PageWrapper>
            <GeneralPopup
                open={!!deletedTestId}
                type={PopupTypes.DELETE}
                title="STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.DELETE_WARNING"
                acceptText="STATIC.BUTTONS.DELETE"
                onAccept={onDeleteConfirm}
                onReject={() => setDeletedTestId(null)}
                loading={deleteAbTestResponse.loading}
            />
        </>
    );
}
